"use client";

import { FC, useEffect, useRef } from "react";
import { z } from "zod";
import { cn } from "@/lib/utils";
import Image from "next/image";
import { formatBackgroundStyle } from "@/utils/styles/format";
import { useIsMobile } from "@/hooks/useIsMobile";

const MetadataSchema = z.object({
  id: z.union([z.string(), z.number()]),
  meta_image: z.string(),
  meta_alt_image: z.string().optional(),
  meta_priority: z.number().optional(),
});

const Placements2DataSchema = z.object({
  status: z.union([z.number(), z.boolean()]).optional().default(true).transform((val) => (typeof val === "number" ? val === 1 : val)),
  id: z.number(),
  page_id: z.number(),
  component_id: z.number(),
  desktop_background_color: z.string().optional().default("#FFFFFF"),
  mobile_background_color: z.string().optional().default("#FFFFFF"),
  backvariant: z.enum(["variant_1", "variant_2", "variant_3"]).optional().default("variant_3"),
  gradient_color_1: z.string().optional().default(""),
  gradient_color_2: z.string().optional().default(""),
  gradient_color_3: z.string().optional().default(""),
  gradient_color_4: z.string().optional().default(""),
  variant: z.string(),
  title: z.string(),
  title_color: z.string().optional().default("#000000"),
  button_text: z.string().optional(),
  button_text_color: z.string().optional().default("#FFFFFF"),
  button_color: z.string().optional().default("#000000"),
  scroll_frequency: z.union([z.string(), z.number()]).optional().default("3000"),
  metadata: z.array(MetadataSchema).optional().default([]),
});

export const Placements2: FC<any> = (props) => {
  const actualData = props?.data?.data || props?.data || props;
  
  const parsedResult = Placements2DataSchema.safeParse(actualData);

  if (!parsedResult.success) {
    console.error("Placements2 validation failed", parsedResult.error);
    return null;
  }

  const parsedData = parsedResult.data;
  const scrollRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  const {
    title,
    title_color,
    scroll_frequency,
    metadata = [],
  } = parsedData;

  // Get background style
  const bgStyle = formatBackgroundStyle(parsedData, isMobile || false);

  // Auto-scroll logic
  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer || !scroll_frequency) {
      console.log('Auto-scroll not initialized:', { hasContainer: !!scrollContainer, scroll_frequency });
      return;
    }

    const scrollSpeed = typeof scroll_frequency === 'string' ? parseInt(scroll_frequency) : scroll_frequency;
    
    console.log('Auto-scroll setup:', {
      scrollSpeed,
      scrollWidth: scrollContainer.scrollWidth,
      clientWidth: scrollContainer.clientWidth,
      canScroll: scrollContainer.scrollWidth > scrollContainer.clientWidth
    });
    
    const scrollStep = () => {
      if (scrollContainer) {
        scrollContainer.scrollLeft += 1;
        
        // Reset to beginning when reaching the end
        if (scrollContainer.scrollLeft >= scrollContainer.scrollWidth / 2) {
          scrollContainer.scrollLeft = 0;
        }
      }
    };

    const intervalId = setInterval(scrollStep, scrollSpeed / 100);

    return () => clearInterval(intervalId);
  }, [scroll_frequency]);

  // Duplicate metadata for seamless loop
  const duplicatedMetadata = [...metadata, ...metadata];

  return (
    <section
      className={cn(
        "relative w-full overflow-hidden flex justify-center",
        "py-[40px]",
        "sm:px-[60px] sm:py-[40px]",
        "md:px-[60px] md:py-[60px]",
        "lg:px-[80px] lg:py-[60px]",
        "xl:px-[140px] xl:py-[80px]"
      )}
      style={bgStyle}
    >
      <div
        className={cn(
          "flex items-center",
          "w-[354px]",
          "sm:w-[960px]",
          "md:w-[1110px]",
          "lg:w-[1270px]",
          "xl:w-[1471px]",
          "flex-col gap-[16px]",
          "sm:flex-row sm:gap-[24px]",
          "md:flex-row md:gap-[24px]",
          "lg:flex-row lg:gap-[36px]",
          "xl:flex-row xl:gap-[40px]"
        )}
      >
        {/* Title Section */}
        <div className={cn(
          "relative flex flex-col shrink-0",
          "w-full",
          "sm:w-[177px]",
          "md:w-[177px]",
          "lg:w-[152px]",
          "xl:w-[133px]"
        )}>
          <h2
            className={cn(
              "font-denton-test font-[800] italic leading-[1.4]",
              "text-[24px] tracking-[0.24px] text-center",
              "sm:text-[21px] sm:tracking-[0.21px] sm:text-left",
              "md:text-[24px] md:tracking-[0.24px] md:text-left",
              "lg:text-[28px] lg:tracking-[0.28px] lg:text-left",
              "xl:text-[28px] xl:tracking-[0.28px] xl:text-left"
            )}
            style={{ color: title_color || "#FFFFFF" }}
          >
            {title}
          </h2>
        </div>

        {/* Ticker/Carousel Section */}
        <div className={cn(
          "relative overflow-hidden",
          "w-[354px]",
          "sm:flex-1 sm:min-w-0 sm:w-auto"
        )}>
          <div
            ref={scrollRef}
            className={cn(
              "flex items-center overflow-x-auto overflow-y-hidden scrollbar-hide",
              "h-[60px]",
              "sm:h-[100px]",
              "md:h-[100px]",
              "lg:h-[100px]",
              "xl:h-[100px]"
            )}
            style={{
              scrollBehavior: 'auto',
              WebkitOverflowScrolling: 'touch',
            }}
          >
            <div
              className={cn(
                "flex items-center shrink-0",
                "gap-[40px]",
                "sm:gap-[80px]",
                "md:gap-[80px]",
                "lg:gap-[80px]",
                "xl:gap-[80px]"
              )}
            >
              {duplicatedMetadata.map((item, idx) => (
                <div
                  key={`${item.id}-${idx}`}
                  className={cn(
                    "relative shrink-0 flex items-center",
                    "h-[20px] min-w-[60px]",
                    "sm:h-[40px] sm:min-w-[100px]",
                    "md:h-[40px] md:min-w-[100px]",
                    "lg:h-[40px] lg:min-w-[100px]",
                    "xl:h-[40px] xl:min-w-[100px]"
                  )}
                >
                  {item.meta_image && (
                    <Image
                      src={item.meta_image}
                      alt={item.meta_alt_image || "Company logo"}
                      width={150}
                      height={40}
                      className="h-full w-auto object-contain max-w-none"
                    />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Gradient overlay for fade effect on edges */}
          <div
            className="absolute top-0 left-0 w-full h-full pointer-events-none"
            style={{
              backgroundImage: isMobile
                ? "linear-gradient(90deg, rgb(0, 0, 0) 0%, rgba(0, 0, 0, 0) 10%, rgba(0, 0, 0, 0) 90%, rgb(0, 0, 0) 100%)"
                : "linear-gradient(90deg, rgb(0, 0, 0) 0%, rgba(0, 0, 0, 0) 25.621%, rgba(0, 0, 0, 0) 77.399%, rgb(0, 0, 0) 100%)",
            }}
          />
        </div>
      </div>
    </section>
  );
};
